import type { ProjectDetail, ProjectStatus } from '@stores/project/types';
import type { WegicRequestConfig } from '@wegic/request';
import type { Pagination } from './index';
import { agentsRequest } from '@/services/request';

export type TrashSortBy = 'deletedAt' | 'expireAt' | 'name';
export type TrashSortOrder = 'ASC' | 'DESC';

export interface TrashListQuery {
  page?: number;
  pageSize?: number;
  keyword?: string;
  sortBy?: TrashSortBy;
  sortOrder?: TrashSortOrder;
}

export interface TrashItem {
  projectId: string;
  name: string;
  status: ProjectStatus;
  coverUrl: string | null;
  summary: string | null;
  /** 移入回收站的时间（ISO 字符串） */
  deletedAt: string;
  /** 回收站过期时间，过后将被自动清理（ISO 字符串） */
  expireAt: string;
  /** 距 expireAt 还剩多少天，向上取整，最小 0 */
  daysLeft: number;
  /** 是否允许恢复，false 时调用 restore 会返回 410 */
  restorable: boolean;
}

export interface TrashListResponse {
  list: TrashItem[];
  pagination: Pagination;
}

export interface RestoreTrashResponse {
  success: boolean;
  project: ProjectDetail;
}

export interface PurgeTrashResponse {
  success: boolean;
}

export const TRASH_PAGE_SIZE = 20;

/**
 * 获取回收站列表
 */
export async function getTrashList(query: TrashListQuery = {}, config?: WegicRequestConfig) {
  const {
    page = 1,
    pageSize = TRASH_PAGE_SIZE,
    keyword,
    sortBy,
    sortOrder,
  } = query;

  return agentsRequest.get<TrashListResponse>(
    '/projects/v1/trash',
    {
      ...config,
      params: {
        page,
        pageSize,
        ...(keyword ? { keyword } : {}),
        ...(sortBy ? { sortBy } : {}),
        ...(sortOrder ? { sortOrder } : {}),
        ...config?.params,
      },
    },
  );
}

/**
 * 从回收站恢复项目
 */
export async function restoreTrashProject(projectId: string, config?: WegicRequestConfig) {
  return agentsRequest.post<RestoreTrashResponse>(
    `/projects/v1/trash/${projectId}/restore`,
    {},
    config,
  );
}

/**
 * 彻底删除（标记）。真正的物理删除由后端定时任务执行。
 */
export async function purgeTrashProject(projectId: string, config?: WegicRequestConfig) {
  return agentsRequest.delete<PurgeTrashResponse>(
    `/projects/v1/trash/${projectId}`,
    config,
  );
}
